/**
 * Ce que montre le centre du live du masteur (pur) : l'élève regardé, l'activité qui est la sienne, les
 * onglets dans leur ordre, l'onglet ouvert, et ce que le contenu rend. Lu par `<TutorLiveCenter>` ; testé
 * à côté.
 */
import type { UICorrectionFeedback } from "../../../ports/primary/ui/types/studentActivity"
import type {
    UITutorLiveCenterTab,
    UITutorLiveFocus,
    UITutorLiveStudent,
} from "../../../ports/primary/ui/types/tutorLive"
import type { TutorAnnotationVisual } from "../../molecules/TutorAnnotationToolbar/TutorAnnotationToolbar"

/**
 * Ce que rend le contenu du centre : la liste (téléphone), les contrôles, la visio de l'élève, la vue de
 * l'activité, ou rien — une présentation sans activité à montrer ne montre jamais un élève.
 */
export type TutorLiveCenterBody =
    "activities" | "assessments" | "visio" | "profile" | "session" | "activity" | "nothing"

export type TutorLiveCenterView = {
    /**
     * L'élève regardé : celui qu'on a choisi, sinon celui de l'activité ouverte. Pendant une présentation,
     * celui de l'activité présentée, quelle que soit la sélection.
     */
    student?: UITutorLiveStudent
    /** L'activité ouverte, si elle est à lui ; sinon rien (l'écran la change avec la sélection). */
    ownFocus: UITutorLiveFocus | null
    /** Les onglets, dans leur ordre : ceux de l'élève, puis ceux de l'activité. */
    keys: UITutorLiveCenterTab[]
    /**
     * La famille de l'élève : la visio, sa séance, sa fiche, ses contrôles quand on les regarde (la liste au
     * téléphone).
     */
    studentKeys: UITutorLiveCenterTab[]
    /** La famille de l'activité ouverte : ses vues. Vide sans activité. */
    activityKeys: UITutorLiveCenterTab[]
    /** L'onglet ouvert : celui demandé s'il existe, sinon le rendu de son activité, sinon la visio. */
    current: UITutorLiveCenterTab
    /** La vue de l'activité que montre le centre ; `null` quand il montre la liste, la visio ou les contrôles. */
    activityTab: Exclude<UITutorLiveCenterTab, "visio" | "activities" | "assessments" | "profile" | "session"> | null
    /** Ce que rend le contenu (la région que « Présenter » partage). */
    body: TutorLiveCenterBody
}

/**
 * Les élèves voient le centre (pur) : une présentation tourne, **ou elle part** (`pending` : le sélecteur
 * ouvert, la piste en route), ou l'onglet entier part déjà (`centerOnly`). Le départ compte : la piste
 * recadrée est publiée AVANT que la présentation ne soit marquée (`setFocusScreenShare(true)` suit
 * `publishTrack`), et ses premières images partaient avec la correction du champion encore posée. L'ancien
 * `ScreenShareToggleButton.web.js` avait le même ordre (la correction de `TodoModal` ne disparaissait
 * qu'avec `isFocusScreenShare`, après la publication) : le centre la cache dès le départ. Un départ
 * annulé (le sélecteur refermé) la rend aussitôt.
 */
export function tutorLiveCenterWatched(input: {
    present?: { on: boolean; pending?: boolean }
    centerOnly: boolean
}): boolean {
    const { present, centerOnly } = input
    return present?.on === true || present?.pending === true || centerOnly
}

/** Ce qu'une présentation peut montrer au groupe. */
const SHOWABLE: UITutorLiveCenterTab[] = ["statement", "work", "whiteboard"]

export function tutorLiveCenterView(input: {
    students: UITutorLiveStudent[]
    selectedId?: string
    focus?: UITutorLiveFocus | null
    tab: UITutorLiveCenterTab
    phone: boolean
    /**
     * Les élèves voient le centre (une présentation tourne, ou l'onglet entier part déjà) : seul ce qui se
     * montre au groupe reste (l'énoncé, le rendu, le tableau), et jamais la visio, la liste ni les
     * contrôles d'un élève — même sans activité à montrer.
     */
    presenting?: boolean
    /**
     * La fiche de l'élève peut se montrer au centre : l'onglet « Profil », dans la famille de l'élève (pas au
     * téléphone, où elle couvre l'écran). Défaut `false`.
     */
    profile?: boolean
    /**
     * La séance de l'élève peut se relire au centre : l'onglet « Fil » (sa séance, ligne par ligne), après
     * la visio. Pas au téléphone. Défaut `false`.
     */
    session?: boolean
    /**
     * Hors live (« Mes élèves » dans le cadre du live) : pas de visio. La famille de l'élève est sa fiche (et ses
     * contrôles quand on les regarde) ; sans activité ouverte, le centre montre la fiche. Défaut `false`.
     */
    offLive?: boolean
}): TutorLiveCenterView {
    const {
        students,
        selectedId,
        focus,
        tab,
        phone,
        presenting = false,
        profile = false,
        session = false,
        offLive = false,
    } = input
    const byId = (id?: string) => (id != null ? students.find((one) => one.id === id) : undefined)
    // L'élève regardé fait foi : la tête, la liste du téléphone et leurs gestes parlent de lui. Pendant une
    // présentation, l'activité présentée fait foi : toucher un autre élève ne la remplace pas par sa visio
    // (l'ancien `TodoModal` recouvrait la salle, on ne pouvait pas toucher un autre élève).
    const student = (presenting ? byId(focus?.studentId) : undefined) ?? byId(selectedId) ?? byId(focus?.studentId)
    const ownFocus = focus != null && focus.studentId === student?.id ? focus : null
    // La famille de l'élève : toujours là. Sa fiche est un onglet (live du 04/11 : « sa place est au centre »).
    // Hors live, ni visio ni liste (la liste est à gauche) : la fiche seule.
    let studentKeys: UITutorLiveCenterTab[] = offLive ? [] : phone ? ["activities", "visio"] : ["visio"]
    if (session && !phone && student != null) studentKeys.push("session")
    if (profile && !phone && student != null) studentKeys.push("profile")
    // Les contrôles : un onglet tant qu'on les regarde, chez un élève qui en a (pas un stage).
    if (tab === "assessments" && student != null && student.nextAssessment !== undefined)
        studentKeys.push("assessments")
    // La famille de l'activité : seulement avec une activité ouverte.
    let activityKeys: UITutorLiveCenterTab[] = []
    if (ownFocus != null) {
        const views: UITutorLiveCenterTab[] = ["statement", "work"]
        if (ownFocus.quiz !== undefined) views.unshift("quiz")
        if (ownFocus.whiteboard === true) views.unshift("whiteboard")
        // Au téléphone, l'ordre s'inverse : le rendu passe devant, c'est lui qui arrive (Wajih, 02/11).
        activityKeys = phone ? views.reverse() : views
    }
    // Pendant une présentation, les élèves voient le centre : ni la visio d'un élève, ni sa fiche, ni ses
    // contrôles, ni son quiz, ni sa liste — avec ou sans activité à montrer.
    if (presenting) {
        studentKeys = studentKeys.filter((key) => SHOWABLE.includes(key))
        activityKeys = activityKeys.filter((key) => SHOWABLE.includes(key))
    }
    const keys = [...studentKeys, ...activityKeys]
    // Hors live sans activité ouverte, la fiche quand elle est offerte ; sans elle, rien (jamais une visio).
    const fallback: UITutorLiveCenterTab =
        ownFocus != null ? "work" : offLive && studentKeys.includes("profile") ? "profile" : "visio"
    const current: UITutorLiveCenterTab = keys.includes(tab) ? tab : fallback
    const activityTab =
        ownFocus != null && activityKeys.includes(current)
            ? (current as NonNullable<TutorLiveCenterView["activityTab"]>)
            : null
    return {
        student,
        ownFocus,
        keys,
        studentKeys,
        activityKeys,
        current,
        activityTab,
        body: bodyOf({ presenting, offLive, student, current, activityTab }),
    }
}

function bodyOf(args: {
    presenting: boolean
    offLive: boolean
    student?: UITutorLiveStudent
    current: UITutorLiveCenterTab
    activityTab: TutorLiveCenterView["activityTab"]
}): TutorLiveCenterBody {
    const { presenting, offLive, student, current, activityTab } = args
    if (activityTab != null) return "activity"
    // Le filet : présenter n'offre que l'activité ; sans elle, la région reste vide plutôt que de partager
    // la vidéo et le prénom d'un élève.
    if (presenting || student == null) return "nothing"
    if (current === "activities") return "activities"
    if (current === "assessments") return "assessments"
    if (current === "profile") return "profile"
    if (current === "session") return "session"
    return offLive ? "nothing" : "visio"
}

/**
 * Le visuel du tableau blanc dans la barre de dessin, pendant une présentation (pur). Le tableau part chez
 * les élèves : sa bascule « Visuel » et l'avis du masteur n'y sont plus posés (`<TutorLiveActivityView
 * presenting>`), ils passent dans la barre — l'ancien `WhiteboardTab` les gardait pendant la présentation
 * (`FloatingControls` : l'œil ; `FeedbackRow` : les pouces, tant que l'image est montrée). Seulement sur le
 * tableau blanc, un visuel prêt, et la bascule offerte ; hors présentation, la vue les porte elle-même.
 */
export function presentationVisual(input: {
    presenting: boolean
    view: "statement" | "work" | "split" | "quiz" | "whiteboard" | null
    visual: UITutorLiveFocus["visual"]
    onToggle?: () => void
    onFeedback?: (feedback: UICorrectionFeedback) => void
}): TutorAnnotationVisual | undefined {
    const { presenting, view, visual, onToggle, onFeedback } = input
    if (!presenting || view !== "whiteboard" || visual?.state !== "ready" || onToggle == null) return undefined
    return {
        shown: visual.shown,
        onToggle,
        feedback:
            visual.shown && visual.feedback !== undefined && onFeedback != null
                ? { value: visual.feedback, onChange: onFeedback }
                : undefined,
    }
}
